<template>
  <div
    ref="chartdiv"
    class="hello"
    :class="narrow ? 'narrowChart' : 'wideChart'"
  ></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  props: {
    report: {
      type: Object,
      default: null,
    },

    narrow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chart: null,
      graph: {
        type: [],
        list: [],
      },
      blocked: {
        censored: {
          self: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
          manager: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
          coworker: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
        },
        nin: {
          self: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
          manager: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
          coworker: {
            flag: false,
            pulse1: false,
            pulse2: false,
            pulse3: false,
          },
        },
      },
    };
  },

  computed: {
    reportCheck() {
      let flag = false;
      if (this.report) {
        flag = true;
      }
      return flag;
    },

    fontColor() {
      return getComputedStyle(document.getElementById('app')).getPropertyValue('--themeFontColor');
    },
  },

  mounted() {
    if (this.reportCheck) {
      this.dataInit();
    }
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },

  methods: {
    dataInit() {
      // Pulse data
      const pulseList = Object.keys(this.report).filter((key) => {
        const nameList = ['pulse1', 'pulse2', 'pulse3'];
        let flag = false;
        nameList.forEach((name) => {
          if (key.includes('pulse') && key === name) { flag = true; }
        });
        return flag;
      });
      pulseList.forEach((pulse) => {
        if (Object.entries(this.report[pulse]).length > 0) {
          this.graph.type.push(pulse);
          const short = this.report[pulse];

          Object.keys(short).forEach((role) => {
            let unit = {};
            if (short[role] === 'CENSORED') {
              let key = role;
              if (['stakeholder', 'stakeholders', 'coworkers'].includes(key)) {
                key = 'coworker';
              }

              unit = {
                role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
                [pulse]: 0,
              };

              this.blocked.censored[key].flag = true;
              this.blocked.censored[key][pulse] = true;
            } else if ((short[role] === 'NIN') || (short[role] === 0)) {
              let key = role;
              if (['stakeholder', 'stakeholders', 'coworkers'].includes(key)) {
                key = 'coworker';
              }
              unit = {
                role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
                [pulse]: 0,
              };

              this.blocked.nin[key].flag = true;
              this.blocked.nin[key][pulse] = true;
            } else {
              let key = role;
              const score = short[key];
              if (['stakeholder', 'stakeholders', 'coworkers'].includes(key)) {
                key = 'coworker';
              }
              unit = {
                role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
                [pulse]: score || 0,
              };
            }
            this.graph.list.push(unit);
          });
        }
      });

      const order = {
        Self: 1,
        Manager: 2,
        Coworker: 3,
        Stakeholder: 3,
      };
      this.graph.list = this.graph.list
        .sort((a, b) => order[a.role] - order[b.role])
        .map((el) => {
          const e = el;
          if (e.role === 'Stakeholder') {
            e.role = this.$t('pulse.Coworkers');
          } else {
            e.role = this.$t(`pulse.${e.role}`);
          }
          return e;
        });

      if (!this.graph.type.length) {
        this.graph.type.push('pulse1');
        this.graph.list = [
          { role: this.$t('pulse.Self'), pulse1: 0 },
          { role: this.$t('pulse.Manager'), pulse1: 0 },
          { role: this.$t('pulse.Coworkers'), pulse1: 0 },
        ];
      }

      this.drawGraph();
    },

    drawGraph() {
      am4core.addLicense(process.env.VUE_APP_AMCHARTS_LICENSE);
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      this.chart.data = this.graph.list;
      this.chart.paddingRight = 30;
      this.chart.paddingLeft = -60;

      // Add chart background color
      this.chart.plotContainer.background.fill = am4core.color(this.fontColor);
      this.chart.plotContainer.background.fillOpacity = 0.1;

      this.createX();
      this.createY();
      this.graph.type.forEach((item, index) => {
        this.createSeries(item, index + 1);
      });
    },

    createX() {
      // Create Y axes
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());

      // Grid
      valueAxis.min = 0;
      valueAxis.max = 5;
      valueAxis.renderer.minGridDistance = 60;
      valueAxis.renderer.maxGridDistance = 120;
      valueAxis.strictMinMax = true;

      // // Label
      valueAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      valueAxis.renderer.labels.template.wrap = true;
      valueAxis.renderer.labels.template.maxWidth = 80;
      valueAxis.renderer.labels.template.textAlign = 'middle';
      valueAxis.renderer.labels.template.fontSize = 10;
      valueAxis.renderer.labels.template.paddingRight = 8;
      valueAxis.renderer.labels.template.adapter.add('text', (label, target) => {
        const desc = {
          value0: '',
          value1: this.$t('pulse.score_1'),
          value2: this.$t('pulse.score_2'),
          value3: this.$t('pulse.score_3'),
          value4: this.$t('pulse.score_4'),
          value5: this.$t('pulse.score_5'),
        };
        if (target.dataItem && desc[`value${target.dataItem.value}`]) {
          return `${label.charAt(0)}\n${desc[`value${target.dataItem.value}`]}`;
        }
        return '';
      });

      valueAxis.renderer.baseGrid.stroke = am4core.color(this.fontColor);
      valueAxis.renderer.baseGrid.strokeOpacity = 1;
      valueAxis.renderer.baseGrid.strokeWidth = 1.5;

      // valueAxis.renderer.grid.template.stroke = am4core.color('#fff');
      valueAxis.renderer.grid.template.strokeOpacity = 0;
      // valueAxis.renderer.grid.template.strokeDasharray = 3;
    },

    createY() {
      // Create X axes
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());

      // // Label
      categoryAxis.dataFields.category = 'role';
      categoryAxis.renderer.inversed = true;
      categoryAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      categoryAxis.renderer.labels.template.wrap = true;
      categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.renderer.labels.template.dx = 7;
      categoryAxis.renderer.labels.template.adapter.add('text', (label, target) => {
        if (target.dataItem && label && !(label.includes('improvement') || label.includes('display'))) {
          /* eslint-disable */
          target.horizontalCenter = 'middle';
          target.rotation = -90;
          target.fontSize = 10;
          target.maxWidth = 1;
          target.maxHeight = 1;
          /* eslint-enable */
        }
        return label;
      });

      // // Grid
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      categoryAxis.renderer.grid.template.strokeOpacity = 0.5;

      // Censored, NIN Range background
      ['censored', 'nin'].forEach((option) => {
        Object.keys(this.blocked[option]).forEach((role) => {
          if (this.blocked[option][role].flag) {
            Object.keys(this.blocked[option][role]).forEach((key) => {
              if (key !== 'flag' && this.blocked[option][role][key]) {
                // // Style setup
                const range = categoryAxis.axisRanges.create();
                range.axisFill.fill = am4core.color(this.fontColor);
                range.axisFill.fillOpacity = 0.1;
                range.axisFill.above = true;
                range.label.fillOpacity = 0.5;
                range.label.fill = am4core.color(this.fontColor);
                range.label.inside = true;
                range.label.valign = 'middle';
                range.label.textAlign = 'middle';
                range.label.fontSize = '11px';

                // // Range
                range.category = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
                range.endCategory = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
                range.label.text = (option === 'censored')
                  ? this.$t('pulse.censored') : this.$t('pulse.nin');

                if (this.graph.type.length !== 1) {
                  if (key === 'pulse1') {
                    // // End line
                    range.locations.endCategory = (1 / this.graph.type.length);
                    range.locations.endValue = (1 / this.graph.type.length);
                  } else if (key === 'pulse2') {
                    // // Start line, End line
                    range.locations.category = (1 / this.graph.type.length);
                    range.locations.endCategory = (1 / this.graph.type.length) * 2;
                    range.locations.value = (1 / this.graph.type.length);
                    range.locations.endValue = (1 / this.graph.type.length) * 2;
                  } else if (key === 'pulse3') {
                    // // Start line
                    range.locations.category = (1 / this.graph.type.length) * 2;
                    range.locations.value = (1 / this.graph.type.length) * 2;
                  }
                }
              }
            });
          }
        });
      });
    },

    createSeries(pulse, index) {
      // Create series
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = pulse;
      series.dataFields.categoryY = 'role';
      series.name = `Pulse${index}`;
      series.columns.template.fillOpacity = 1;
      let barColor = '#f3c065';
      if (index === 2) {
        barColor = '#00ace2';
      } else if (index === 3) {
        barColor = '#8d33be';
      }
      series.columns.template.fill = am4core.color(barColor);
      series.columns.template.width = am4core.percent(40);

      series.columns.template.tooltipText = '{name} {categoryY}: [bold]{valueX}[/]';
      series.tooltip.background.strokeOpacity = 0;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color('white');
      series.tooltip.label.fill = am4core.color('black');

      const columnTemplate = series.columns.template;
      columnTemplate.strokeOpacity = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.hello {
  position: relative;
  width: 100%;
  height: 320px;
  font-size: 12px;
  font-style: normal;

  div:first-of-type {
    top: 0 !important;
    left: 0 !important;
  }

  &::before {
    position: absolute;
    content: '';
    top: calc(5% - 2px);
    width: 21px;
    height: calc(100% - 86px);
    background: rgba(255, 255, 255, 0.2);
    box-sizing: border-box;
  }

  &.narrowChart {
    &::before {
      left: 21px;
    }
  }

  &.wideChart {
    &::before {
      left: 19px;
    }
  }
}
</style>
